import React, {
  useState,
  useEffect,
  useRef,
  useContext,
  useCallback,
} from "react";

import { useHistory, useParams } from "react-router-dom";
import { parseISO, format, isSameDay, formatDistanceToNow, differenceInDays } from "date-fns";
import { es } from "date-fns/locale";
import clsx from "clsx";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import { green, grey } from "@material-ui/core/colors";
import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import MarkdownWrapper from "../MarkdownWrapper";
import { List, Tooltip } from "@material-ui/core";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import toastError from "../../errors/toastError";
import { v4 as uuidv4 } from "uuid";

import GroupIcon from "@material-ui/icons/Group";
import ContactTag from "../ContactTag";
import ConnectionIcon from "../ConnectionIcon";
import AcceptTicketWithouSelectQueue from "../AcceptTicketWithoutQueueModal";
import TransferTicketModalCustom from "../TransferTicketModalCustom";
import ShowTicketOpen from "../ShowTicketOpenModal";
import FinalizacaoVendaModal from "../FinalizacaoVendaModal";
import { isNil } from "lodash";
import { toast } from "react-toastify";
import { Done, HighlightOff, SwapHoriz, Add } from "@material-ui/icons";
import VisibilityIcon from "@material-ui/icons/Visibility"; // Ícone de spy
import useCompanySettings from "../../hooks/useSettings/companySettings";
import NewTicketModal from "../NewTicketModal";
import {
  Avatar,
  Badge,
  ListItemAvatar,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Typography,
  Dialog,
  DialogTitle,
  DialogActions,
  Button,
  DialogContent,
} from "@material-ui/core";

const useStyles = makeStyles((theme) => ({
  ticket: {
    position: "relative",
    padding: "12px 12px 10px 16px",
    margin: "4px 8px",
    borderRadius: 14,
    border: "1px solid",
    borderColor: theme.mode === "dark" ? "rgba(255,255,255,0.09)" : "rgba(0,0,0,0.07)",
    background: theme.mode === "dark"
      ? "linear-gradient(135deg, rgba(15,23,42,0.95) 0%, rgba(22,32,52,0.90) 100%)"
      : "#ffffff",
    boxShadow: theme.mode === "dark"
      ? "0 2px 8px rgba(0,0,0,0.30)"
      : "0 1px 4px rgba(0,0,0,0.06)",
    transition: "all 0.18s ease",
    "&:hover": {
      background: theme.mode === "dark"
        ? "linear-gradient(135deg, rgba(91,45,142,0.12) 0%, rgba(91,45,142,0.06) 100%)"
        : "rgba(91,45,142,0.04)",
      borderColor: "rgba(91,45,142,0.35)",
      boxShadow: theme.mode === "dark"
        ? "0 4px 16px rgba(91,45,142,0.20)"
        : "0 4px 12px rgba(91,45,142,0.12)",
      transform: "translateY(-1px)",
    },
    "&.Mui-selected, &.Mui-selected:hover": {
      background: theme.mode === "dark"
        ? "linear-gradient(135deg, rgba(91,45,142,0.20) 0%, rgba(91,45,142,0.10) 100%)"
        : "rgba(91,45,142,0.07)",
      borderColor: "#5B2D8E",
      boxShadow: theme.mode === "dark"
        ? "0 4px 20px rgba(91,45,142,0.25), inset 0 0 0 1px rgba(91,45,142,0.30)"
        : "0 4px 12px rgba(91,45,142,0.15)",
    },
  },

  pendingTicket: {
    cursor: "unset",
  },

  ticketQueueColor: {
    width: 4,
    height: "60%",
    position: "absolute",
    top: "20%",
    left: 0,
    borderRadius: "0 3px 3px 0",
  },

  // Fila nombre + hora
  topRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    marginBottom: 3,
  },

  contactName: {
    fontWeight: 700,
    fontSize: 13.5,
    color: theme.mode === "dark" ? "#e2e8f0" : "#1e293b",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "calc(100% - 80px)",
    display: "flex",
    alignItems: "center",
    gap: 5,
  },

  timeLabel: {
    fontSize: 11,
    color: theme.mode === "dark" ? "#475569" : "#94a3b8",
    whiteSpace: "nowrap",
    flexShrink: 0,
  },

  timeLabelUnread: {
    fontSize: 11,
    fontWeight: 700,
    color: "#7C3AED",
    whiteSpace: "nowrap",
    flexShrink: 0,
  },

  // Preview último mensaje
  lastMessage: {
    display: "block",
    fontSize: 12,
    color: theme.mode === "dark" ? "#475569" : "#94a3b8",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "100%",
    marginBottom: 6,
  },

  lastMessageUnread: {
    display: "block",
    fontSize: 12,
    fontWeight: 600,
    color: theme.mode === "dark" ? "#94a3b8" : "#475569",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "100%",
    marginBottom: 6,
  },

  // Badge mensajes no leídos
  unreadBadge: {
    minWidth: 20,
    height: 20,
    borderRadius: 10,
    background: "linear-gradient(135deg, #5B2D8E, #7C3AED)",
    color: "#fff",
    fontSize: 10,
    fontWeight: 800,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 5px",
    flexShrink: 0,
    boxShadow: "0 2px 6px rgba(91,45,142,0.40)",
  },

  // Fila de tags + botones
  tagsRow: {
    display: "flex",
    alignItems: "center",
    flexWrap: "nowrap",
    gap: 4,
    overflow: "hidden",
  },

  tagsGroup: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 3,
    flex: 1,
    overflow: "hidden",
    minWidth: 0,
  },

  tag: {
    display: "inline-flex",
    alignItems: "center",
    padding: "2px 7px",
    borderRadius: 5,
    fontSize: 10,
    fontWeight: 700,
    color: "rgba(255,255,255,0.95)",
    letterSpacing: "0.03em",
    maxWidth: 100,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    textShadow: "0 1px 2px rgba(0,0,0,0.25)",
  },

  // Botones de acción
  actionButtons: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    flexShrink: 0,
    marginLeft: 6,
  },

  actionBtn: {
    padding: 0,
    borderRadius: 8,
    minWidth: "auto",
    width: 34,
    height: 34,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1px solid",
    borderColor: theme.mode === "dark" ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.10)",
    background: theme.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)",
    cursor: "pointer",
    transition: "all 0.15s ease",
    "&:hover": {
      background: theme.mode === "dark" ? "rgba(91,45,142,0.20)" : "rgba(91,45,142,0.10)",
      borderColor: "rgba(91,45,142,0.40)",
      transform: "scale(1.08)",
    },
  },

  queueTag: {
    background: "#FCFCFC",
    color: "#000",
    marginRight: 1,
    padding: 1,
    fontWeight: "bold",
    borderRadius: 3,
    fontSize: "0.5em",
    whiteSpace: "nowrap",
  },
  noTicketsDiv: {
    display: "flex",
    height: "100px",
    margin: 40,
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  noTicketsText: {
    textAlign: "center",
    color: "rgb(104, 121, 146)",
    fontSize: "14px",
    lineHeight: "1.4",
  },
  connectionTag: {
    background: "green",
    color: "#FFF",
    marginRight: 1,
    padding: 1,
    fontWeight: "bold",
    borderRadius: 3,
    fontSize: "0.6em",
  },
  noTicketsTitle: {
    textAlign: "center",
    fontSize: "16px",
    fontWeight: "600",
    margin: "0px",
  },
  contactNameWrapper: {
    display: "flex",
    justifyContent: "space-between",
    marginLeft: "5px",
    fontWeight: "bold",
    color: theme.mode === "light" ? "black" : "white",
  },
  lastMessageTime: {
    justifySelf: "flex-end",
    textAlign: "right",
    position: "relative",
    top: -30,
    marginRight: "1px",
    color: theme.mode === "light" ? "black" : grey[400],
  },
  lastMessageTimeUnread: {
    justifySelf: "flex-end",
    textAlign: "right",
    position: "relative",
    top: -30,
    color: "green",
    fontWeight: "bold",
    marginRight: "1px",
  },
  closedBadge: {
    alignSelf: "center",
    justifySelf: "flex-end",
    marginRight: 32,
    marginLeft: "auto",
  },
  contactLastMessage: {
    paddingRight: "0%",
    marginLeft: "5px",
    color: theme.mode === "light" ? "black" : grey[400],
  },
  contactLastMessageUnread: {
    paddingRight: 20,
    fontWeight: "bold",
    color: theme.mode === "light" ? "black" : grey[400],
  },
  badgeStyle: {
    color: "white",
    backgroundColor: green[500],
  },
  acceptButton: {
    position: "absolute",
    right: "1px",
  },
  newMessagesCount: {
    justifySelf: "flex-end",
    textAlign: "right",
    position: "relative",
    top: 0,
    color: "green",
    fontWeight: "bold",
    marginRight: "10px",
    borderRadius: 0,
  },
  ticketInfo: {
    position: "relative",
    top: -13,
  },
  secondaryContentSecond: {
    display: "flex",
    alignItems: "flex-start",
    flexWrap: "nowrap",
    flexDirection: "row",
    alignContent: "flex-start",
  },
  ticketInfo1: {
    position: "relative",
    top: 13,
    right: 0,
  },
  Radiusdot: {
    "& .MuiBadge-badge": {
      borderRadius: 2,
      position: "inherit",
      height: 16,
      margin: 2,
      padding: 3,
    },
    "& .MuiBadge-anchorOriginTopRightRectangle": {
      transform: "scale(1) translate(0%, -40%)",
    },
  },
  connectionIcon: {
    marginRight: theme.spacing(1),
  },
  imageModal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  imageModalContent: {
    outline: "none",
    maxWidth: "90vw",
    maxHeight: "90vh",
  },
  expandedImage: {
    width: "100%",
    height: "auto",
    maxWidth: "500px",
    borderRadius: theme.spacing(1),
  },
  clickableAvatar: {
    cursor: "pointer",
    "&:hover": {
      opacity: 0.8,
    },
  },
  secondaryContentSecond1: {
    display: "flex",
    alignItems: "flex-start",
    flexWrap: "nowrap",
    flexDirection: "row",
    alignContent: "flex-start",
  },

  waitBadge: {
    position: "absolute",
    top: -4,
    left: -4,
    minWidth: 18,
    height: 18,
    borderRadius: 9,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 9,
    fontWeight: 800,
    padding: "0 4px",
    border: `1.5px solid ${theme.mode === "dark" ? "#0d0818" : "#fff"}`,
    zIndex: 1,
  },

  waitBadgeNormal: {
    background: "#facc15",
    color: "#000",
  },

  waitBadgeUrgent: {
    background: "#f87171",
    color: "#fff",
  },

  avatarWrapper: {
    position: "relative",
  },

  closeBtnHover: {
    opacity: 0,
    transition: "opacity 0.15s ease",
    "$ticket:hover &": {
      opacity: 1,
    },
  },
}));

const formatContactName = (name) => {
  if (!name) return "";
  const cleaned = name.replace(/[\s\-\(\)]/g, "");
  if (cleaned.length >= 8 && /^[\+\d]+$/.test(cleaned)) {
    const digits = name.replace(/\D/g, "");
    if (digits.length === 13) return `+${digits.slice(0,2)} ${digits.slice(2,4)} ${digits.slice(4,8)} ${digits.slice(8)}`;
    if (digits.length === 12) return `+${digits.slice(0,2)} ${digits.slice(2,4)} ${digits.slice(4,8)} ${digits.slice(8)}`;
    if (digits.length === 11) return `+${digits.slice(0,2)} ${digits.slice(2,7)} ${digits.slice(7)}`;
    if (digits.length === 10) return `+${digits.slice(0,2)} ${digits.slice(2,6)} ${digits.slice(6)}`;
    return name;
  }
  return name;
};

const isPhoneNumber = (name) => {
  if (!name) return false;
  const cleaned = name.replace(/[\s\-\(\)]/g, "");
  return cleaned.length >= 8 && /^[\+\d]+$/.test(cleaned);
};

const getRelativeTime = (dateStr) => {
  if (!dateStr) return "";
  const date = parseISO(dateStr);
  const now = new Date();
  const diffDays = differenceInDays(now, date);
  if (diffDays === 0) return format(date, "HH:mm");
  if (diffDays === 1) return "ayer";
  if (diffDays < 7) return `hace ${diffDays} días`;
  return formatDistanceToNow(date, { locale: es, addSuffix: true });
};

const getWaitDays = (createdAt) => {
  if (!createdAt) return 0;
  return differenceInDays(new Date(), parseISO(createdAt));
};

const TicketListItemCustom = ({ setTabOpen, ticket }) => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [
    acceptTicketWithouSelectQueueOpen,
    setAcceptTicketWithouSelectQueueOpen,
  ] = useState(false);
  const [transferTicketModalOpen, setTransferTicketModalOpen] = useState(false);
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);

  const [openAlert, setOpenAlert] = useState(false);
  const [userTicketOpen, setUserTicketOpen] = useState("");
  const [queueTicketOpen, setQueueTicketOpen] = useState("");

  // Estados para o modal de finalização de venda
  const [openFinalizacaoVenda, setOpenFinalizacaoVenda] = useState(false);
  const [finalizacaoTipo, setFinalizacaoTipo] = useState(null);
  const [ticketDataToFinalize, setTicketDataToFinalize] = useState(null);
  const [showFinalizacaoOptions, setShowFinalizacaoOptions] = useState(false);

  const [imageModalOpen, setImageModalOpen] = useState(false); // Estado para o modal da imagem

  const { ticketId } = useParams();
  const isMounted = useRef(true);
  const { setCurrentTicket } = useContext(TicketsContext);
  const { user } = useContext(AuthContext);

  const { get: getSetting } = useCompanySettings();

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Função para abrir modal da imagem
  const handleImageClick = (e) => {
    e.stopPropagation(); // Prevenir que o clique no avatar selecione o ticket
    if (ticket?.contact?.urlPicture) {
      setImageModalOpen(true);
    }
  };

  // Função para fechar modal da imagem
  const handleImageModalClose = () => {
    setImageModalOpen(false);
  };

  const handleOpenAcceptTicketWithouSelectQueue = useCallback(() => {
    setAcceptTicketWithouSelectQueueOpen(true);
  }, []);

  const handleCloseTicket = async (id) => {
    // Verificar se a finalização com valor de venda está ativa
    if (
      user.finalizacaoComValorVendaAtiva === true ||
      user.finalizacaoComValorVendaAtiva === "true"
    ) {
      // Se estiver ativa, abrir o modal de finalização de venda
      setFinalizacaoTipo("comDespedida");
      setOpenFinalizacaoVenda(true);
      handleSelectTicket(ticket);
      history.push(`/tickets/${ticket.uuid}`);
    } else {
      // Comportamento original
      const setting = await getSetting({
        column: "requiredTag",
      });

      if (setting.requiredTag === "enabled") {
        //verificar se tem uma tag
        try {
          const contactTags = await api.get(
            `/contactTags/${ticket.contact.id}`
          );
          if (!contactTags.data.tags) {
            toast.warning(i18n.t("messagesList.header.buttons.requiredTag"));
          } else {
            await api.put(`/tickets/${id}`, {
              status: "closed",
              userId: user?.id || null,
            });

            if (isMounted.current) {
              setLoading(false);
            }

            history.push(`/tickets/`);
          }
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      } else {
        setLoading(true);
        try {
          await api.put(`/tickets/${id}`, {
            status: "closed",
            userId: user?.id || null,
          });
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
        if (isMounted.current) {
          setLoading(false);
        }

        history.push(`/tickets/`);
      }
    }
  };

  const handleCloseIgnoreTicket = async (id) => {
    setLoading(true);
    try {
      await api.put(`/tickets/${id}`, {
        status: "closed",
        userId: user?.id || null,
        sendFarewellMessage: false,
        amountUsedBotQueues: 0,
      });
    } catch (err) {
      setLoading(false);
      toastError(err);
    }
    if (isMounted.current) {
      setLoading(false);
    }

    history.push(`/tickets/`);
  };

  const truncate = (str, len) => {
    if (!isNil(str)) {
      if (str.length > len) {
        return str.substring(0, len) + "...";
      }
      return str;
    }
  };

  const handleCloseTransferTicketModal = useCallback(() => {
    if (isMounted.current) {
      setTransferTicketModalOpen(false);
    }
  }, []);

  const handleOpenTransferModal = () => {
    setLoading(true);
    setTransferTicketModalOpen(true);
    if (isMounted.current) {
      setLoading(false);
    }
    handleSelectTicket(ticket);
    history.push(`/tickets/${ticket.uuid}`);
  };

  const handleOpenNewTicketModal = () => {
    setNewTicketModalOpen(true);
  };

  const handleCloseNewTicketModal = (newTicket) => {
    setNewTicketModalOpen(false);
    if (newTicket) {
      // Se um novo ticket foi criado, redirecionar para ele
      handleSelectTicket(newTicket);
      history.push(`/tickets/${newTicket.uuid}`);
    }
  };

  const handleAcepptTicket = async (id) => {
    setLoading(true);
    try {
      const otherTicket = await api.put(`/tickets/${id}`, {
        status:
          ticket.isGroup && ticket.channel === "whatsapp" ? "group" : "open",
        userId: user?.id,
      });

      if (otherTicket.data.id !== ticket.id) {
        if (otherTicket.data.userId !== user?.id) {
          setOpenAlert(true);
          setUserTicketOpen(otherTicket.data.user.name);
          setQueueTicketOpen(otherTicket.data.queue.name);
        } else {
          setLoading(false);
          setTabOpen(ticket.isGroup ? "group" : "open");
          handleSelectTicket(otherTicket.data);
          history.push(`/tickets/${otherTicket.uuid}`);
        }
      } else {
        let setting;

        try {
          setting = await getSetting({
            column: "sendGreetingAccepted",
          });
        } catch (err) {
          toastError(err);
        }

        if (
          setting.sendGreetingAccepted === "enabled" &&
          (!ticket.isGroup || ticket.whatsapp?.groupAsTicket === "enabled")
        ) {
          handleSendMessage(ticket.id);
        }
        if (isMounted.current) {
          setLoading(false);
        }

        setTabOpen(ticket.isGroup ? "group" : "open");
        handleSelectTicket(ticket);
        history.push(`/tickets/${ticket.uuid}`);
      }
    } catch (err) {
      setLoading(false);
      toastError(err);
    }
  };

  const handleSendMessage = async (id) => {
    let setting;

    try {
      setting = await getSetting({
        column: "greetingAcceptedMessage",
      });
    } catch (err) {
      toastError(err);
    }
    if (!setting.greetingAcceptedMessage) {
      toast.warning(
        i18n.t("messagesList.header.buttons.greetingAcceptedMessage")
      );
      return;
    }
    const msg = `${setting.greetingAcceptedMessage}`;
    const message = {
      read: 1,
      fromMe: true,
      mediaUrl: "",
      body: `${msg.trim()}`,
    };
    try {
      await api.post(`/messages/${id}`, message);
    } catch (err) {
      toastError(err);
    }
  };

  const handleCloseAlert = useCallback(() => {
    setOpenAlert(false);
    setLoading(false);
  }, []);

  const handleSelectTicket = (ticket) => {
    const code = uuidv4();
    const { id, uuid } = ticket;
    setCurrentTicket({ id, uuid, code });
  };

  const handleUpdateTicketStatusWithData = async (
    ticketData,
    sendFarewellMessage,
    finalizacaoMessage
  ) => {
    try {
      await api.put(`/tickets/${ticket.id}`, {
        ...ticketData,
        sendFarewellMessage,
        finalizacaoMessage,
      });
      toast.success("Ticket finalizado com sucesso!");
      history.push(`/tickets/`);
    } catch (err) {
      toastError(err);
    }
  };

  // Função para espionar ticket chatbot
  const handleSpyTicket = () => {
    handleSelectTicket(ticket);
    history.push(`/tickets/${ticket.uuid}`);
  };

  // Lógica de permissão para mensagens pending - MOVIDA PARA DEPOIS DE TODAS AS FUNÇÕES
  const shouldBlurMessages = ticket.status === "pending" && user?.allowSeeMessagesInPendingTickets === "disabled";

  // Função para renderizar a mensagem com base na permissão - MOVIDA PARA DEPOIS DE TODAS AS FUNÇÕES
  const renderLastMessage = () => {
    if (shouldBlurMessages) {
      return (
        <MarkdownWrapper>
          {i18n.t("tickets.messageHidden") || "Mensagem oculta"}
        </MarkdownWrapper>
      );
    }

    if (!ticket.lastMessage) {
      return <br />;
    }

    if (ticket.lastMessage.includes("data:image/png;base64")) {
      return <MarkdownWrapper>Ubicación</MarkdownWrapper>;
    }

    if (ticket.lastMessage.includes("BEGIN:VCARD")) {
      return <MarkdownWrapper>Contacto</MarkdownWrapper>;
    }

    return (
      <MarkdownWrapper>
        {truncate(ticket.lastMessage, 40)}
      </MarkdownWrapper>
    );
  };

  return (
    <React.Fragment key={ticket.id}>
      {openAlert && (
        <ShowTicketOpen
          isOpen={openAlert}
          handleClose={handleCloseAlert}
          user={userTicketOpen}
          queue={queueTicketOpen}
        />
      )}
      {acceptTicketWithouSelectQueueOpen && (
        <AcceptTicketWithouSelectQueue
          modalOpen={acceptTicketWithouSelectQueueOpen}
          onClose={(e) => setAcceptTicketWithouSelectQueueOpen(false)}
          ticketId={ticket.id}
          ticket={ticket}
        />
      )}
      {transferTicketModalOpen && (
        <TransferTicketModalCustom
          modalOpen={transferTicketModalOpen}
          onClose={handleCloseTransferTicketModal}
          ticketid={ticket.id}
          ticket={ticket}
        />
      )}
      {newTicketModalOpen && (
        <NewTicketModal
          modalOpen={newTicketModalOpen}
          onClose={handleCloseNewTicketModal}
          initialContact={ticket.contact}
        />
      )}
      <ListItem
        button
        dense
        onClick={(e) => {
          const isCheckboxClicked =
            (e.target.tagName.toLowerCase() === "input" && e.target.type === "checkbox") ||
            (e.target.tagName.toLowerCase() === "svg" && e.target.type === undefined) ||
            (e.target.tagName.toLowerCase() === "path" && e.target.type === undefined);
          if (isCheckboxClicked) return;
          handleSelectTicket(ticket);
        }}
        selected={ticketId && ticketId === ticket.uuid}
        className={clsx(classes.ticket, {
          [classes.pendingTicket]: ticket.status === "pending",
        })}
      >
        {/* Barra de color del departamento */}
        <div
          className={classes.ticketQueueColor}
          style={{ backgroundColor: ticket.queue?.color || "transparent" }}
        />

        {/* Avatar */}
        <ListItemAvatar style={{ minWidth: 52 }}>
          <div className={classes.avatarWrapper}>
            {(() => {
              const waitDays = getWaitDays(ticket.createdAt);
              const isUrgent = waitDays >= 3;
              return waitDays > 0 ? (
                <span className={`${classes.waitBadge} ${isUrgent ? classes.waitBadgeUrgent : classes.waitBadgeNormal}`}>
                  {waitDays}d
                </span>
              ) : null;
            })()}
            <Avatar
              style={{
                width: 44,
                height: 44,
                fontSize: 17,
                fontWeight: 700,
                background: "linear-gradient(135deg, #5B2D8E22, #7C3AED22)",
                border: Number(ticket.unreadMessages) > 0 ? "2px solid #5B2D8E" : "2px solid rgba(91,45,142,0.25)",
                color: "#5B2D8E",
              }}
              src={ticket?.contact?.urlPicture}
              className={classes.clickableAvatar}
              onClick={handleImageClick}
            >
              {ticket.contact?.name?.[0]?.toUpperCase()}
            </Avatar>
          </div>
        </ListItemAvatar>

        {/* Contenido principal */}
        <ListItemText
          disableTypography
          style={{ margin: 0, overflow: "hidden" }}
          primary={
            <div className={classes.topRow}>
              {/* Nombre del contacto */}
              <span
                className={classes.contactName}
                style={isPhoneNumber(ticket.contact?.name) ? { fontFamily: "monospace", fontSize: 12, color: "inherit" } : {}}
              >
                {ticket.channel && (
                  <ConnectionIcon
                    width="13"
                    height="13"
                    connectionType={ticket.channel}
                  />
                )}
                {ticket.isGroup && <GroupIcon style={{ fontSize: 13, color: grey[500] }} />}
                {truncate(formatContactName(ticket.contact?.name), 35)}
              </span>

              {/* Hora relativa + badge no leídos */}
              <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
                <span className={Number(ticket.unreadMessages) > 0 ? classes.timeLabelUnread : classes.timeLabel}>
                  {ticket.updatedAt && getRelativeTime(ticket.updatedAt)}
                </span>
                {Number(ticket.unreadMessages) > 0 && (
                  <span
                    className={classes.unreadBadge}
                    style={!shouldBlurMessages && ticket.unreadMessages > 100 ? { background: "#f87171", boxShadow: "0 2px 6px rgba(248,113,113,0.40)" } : {}}
                  >
                    {shouldBlurMessages ? "?" : ticket.unreadMessages}
                  </span>
                )}
              </div>
            </div>
          }
          secondary={
            <>
              {/* Última mensaje */}
              <span className={Number(ticket.unreadMessages) > 0 ? classes.lastMessageUnread : classes.lastMessage}>
                {renderLastMessage()}
              </span>

              {/* Tags + botones de acción */}
              <div className={classes.tagsRow}>
                {/* Tags compactos */}
                <div className={classes.tagsGroup}>
                  {ticket?.whatsapp && (
                    <span
                      className={classes.tag}
                      style={{
                        backgroundColor:
                          ticket.channel === "whatsapp"
                            ? ticket.whatsapp?.color || "#25D366"
                            : ticket.channel === "facebook"
                            ? "#4267B2"
                            : "#E1306C",
                      }}
                    >
                      {ticket.whatsapp?.name}
                    </span>
                  )}
                  {(ticket.queueId || ticket.status === "lgpd") && (
                    <span
                      className={classes.tag}
                      style={{ backgroundColor: ticket.queue?.color || "#64748b" }}
                    >
                      {ticket.status === "lgpd" ? "LGPD" : ticket.queue?.name}
                    </span>
                  )}
                  {ticket?.user && (
                    <span className={classes.tag} style={{ backgroundColor: "#0f4c81" }}>
                      {ticket.user?.name}
                    </span>
                  )}
                  {ticket?.contact?.tags?.map((tag) => (
                    <ContactTag tag={tag} key={`ctag-${ticket.id}-${tag.id}`} />
                  ))}
                  {ticket.tags?.map((tag) => (
                    <ContactTag tag={tag} key={`ttag-${ticket.id}-${tag.id}`} />
                  ))}
                </div>

                {/* Botones de acción */}
                <div className={classes.actionButtons} onClick={e => e.stopPropagation()}>
                  {ticket.status === "chatbot" && (
                    <Tooltip title="Espiar chatbot">
                      <span>
                        <ButtonWithSpinner loading={loading} className={classes.actionBtn} onClick={handleSpyTicket} size="small">
                          <VisibilityIcon style={{ fontSize: 18, color: "#5B2D8E" }} />
                        </ButtonWithSpinner>
                      </span>
                    </Tooltip>
                  )}
                  {ticket.status !== "chatbot" && (
                    <>
                      {ticket.status === "pending" && (ticket.queueId === null || ticket.queueId === undefined) && (
                        <Tooltip title={i18n.t("ticketsList.buttons.accept")}>
                          <span>
                            <ButtonWithSpinner loading={loading} className={classes.actionBtn} onClick={() => handleOpenAcceptTicketWithouSelectQueue()} size="small">
                              <Done style={{ fontSize: 18, color: "#22c55e" }} />
                            </ButtonWithSpinner>
                          </span>
                        </Tooltip>
                      )}
                      {ticket.status === "pending" && ticket.queueId !== null && (
                        <Tooltip title={i18n.t("ticketsList.buttons.accept")}>
                          <span>
                            <ButtonWithSpinner loading={loading} className={classes.actionBtn} onClick={() => handleAcepptTicket(ticket.id)} size="small">
                              <Done style={{ fontSize: 18, color: "#22c55e" }} />
                            </ButtonWithSpinner>
                          </span>
                        </Tooltip>
                      )}
                      {(ticket.status === "pending" || ticket.status === "open" || ticket.status === "group") && (
                        <Tooltip title={i18n.t("ticketsList.buttons.transfer")}>
                          <span>
                            <ButtonWithSpinner loading={loading} className={classes.actionBtn} onClick={handleOpenTransferModal} size="small">
                              <SwapHoriz style={{ fontSize: 18, color: "#a855f7" }} />
                            </ButtonWithSpinner>
                          </span>
                        </Tooltip>
                      )}
                      {(ticket.status === "open" || ticket.status === "group") && (
                        <Tooltip title={i18n.t("ticketsList.buttons.closed")}>
                          <span className={classes.closeBtnHover}>
                            <ButtonWithSpinner loading={loading} className={classes.actionBtn} onClick={() => handleCloseTicket(ticket.id)} size="small">
                              <HighlightOff style={{ fontSize: 18, color: "#ef4444" }} />
                            </ButtonWithSpinner>
                          </span>
                        </Tooltip>
                      )}
                      {(ticket.status === "pending" || ticket.status === "lgpd") &&
                        (user.userClosePendingTicket === "enabled" || user.profile === "admin") && (
                          <Tooltip title={i18n.t("ticketsList.buttons.ignore")}>
                            <span>
                              <ButtonWithSpinner loading={loading} className={classes.actionBtn} onClick={() => handleCloseIgnoreTicket(ticket.id)} size="small">
                                <HighlightOff style={{ fontSize: 18, color: "#ef4444" }} />
                              </ButtonWithSpinner>
                            </span>
                          </Tooltip>
                        )}
                      {ticket.status === "closed" && (
                        <Tooltip title="Nuevo ticket">
                          <span>
                            <ButtonWithSpinner loading={loading} className={classes.actionBtn} onClick={(e) => { e.stopPropagation(); handleOpenNewTicketModal(); }} size="small">
                              <Add style={{ fontSize: 18, color: "#22c55e" }} />
                            </ButtonWithSpinner>
                          </span>
                        </Tooltip>
                      )}
                    </>
                  )}
                </div>
              </div>
            </>
          }
        />
      </ListItem>

      {/* Modal de Finalização de Venda */}
      {openFinalizacaoVenda && (
        <FinalizacaoVendaModal
          open={openFinalizacaoVenda}
          onClose={() => setOpenFinalizacaoVenda(false)}
          ticket={ticket}
          onFinalizar={(ticketData) => {
            setOpenFinalizacaoVenda(false);
            setTicketDataToFinalize(ticketData);
            setShowFinalizacaoOptions(true);
          }}
        />
      )}

      {/* Modal de Opções de Finalização */}
      {showFinalizacaoOptions && (
        <Dialog
          open={showFinalizacaoOptions}
          onClose={() => setShowFinalizacaoOptions(false)}
          aria-labelledby="finalizacao-options-title"
        >
          <DialogTitle id="finalizacao-options-title">
            Como deseja finalizar?
          </DialogTitle>
          <DialogActions>
            <Button
              onClick={async () => {
                setShowFinalizacaoOptions(false);
                await handleUpdateTicketStatusWithData(
                  ticketDataToFinalize,
                  false,
                  null
                );
              }}
              style={{ background: theme.palette.primary.main, color: "white" }}
            >
              {i18n.t("messagesList.header.dialogRatingWithoutFarewellMsg")}
            </Button>
            <Button
              onClick={async () => {
                setShowFinalizacaoOptions(false);
                await handleUpdateTicketStatusWithData(
                  ticketDataToFinalize,
                  true,
                  null
                );
              }}
              style={{ background: theme.palette.primary.main, color: "white" }}
            >
              {i18n.t("messagesList.header.dialogRatingCancel")}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Modal da Imagem */}
      <Dialog
        open={imageModalOpen}
        onClose={handleImageModalClose}
        className={classes.imageModal}
        maxWidth="md"
        fullWidth
      >
        <DialogContent className={classes.imageModalContent}>
          <img 
            src={ticket?.contact?.urlPicture} 
            alt={ticket?.contact?.name || "Foto do contato"}
            className={classes.expandedImage}
          />
        </DialogContent>
      </Dialog>
    </React.Fragment>
  );
};

export default TicketListItemCustom;